package com.milta.feature.route.domain.entity

import kotlinx.serialization.Serializable

/**
 * State machine of a queued item across one [OfflineSyncRepository.syncPending] replay cycle.
 * [Failed] is transient (network/server error — retried on the next cycle); [Conflict] is
 * terminal (HTTP 409 — the delivery already has a final status server-side, e.g. synced from
 * another device) and is never replayed again, only dismissable by the driver.
 */
@Serializable
enum class PendingSyncState { Pending, Syncing, Failed, Conflict }

/**
 * A delivery validation captured while offline (MIL-43, `docs/mockups/MIL-43.html`), queued for
 * later replay against `POST /routes/{routeId}/deliveries/{deliveryId}/validate` — the exact same
 * endpoint MIL-37/41 already uses online, unchanged apart from the new optional `occurredAt` form
 * field. [occurredAt] is the ISO-8601 instant of the local "Enregistrer" tap — sent back on replay
 * so `DeliveryProof.completedAt` reflects the real capture time, not the (possibly much later)
 * instant the replay actually succeeds.
 *
 * The raw photo [ByteArray] is deliberately NOT a field here — same precedent as
 * `DeliveryProofState` keeping `photoBytes` out of its observable state (a `ByteArray` field would
 * break structural equality every time this queue, observed as a `StateFlow`, is compared, and
 * bloat the persisted JSON list). The bytes are persisted separately (Base64, one
 * `LocalCacheStore` entry per item) by
 * [com.milta.feature.route.data.local.PendingDeliveryValidationLocalDataSource], keyed by [id].
 *
 * `@Serializable` — the whole queue is persisted as one JSON blob in `LocalCacheStore` so it
 * survives the app process being killed while offline (previously in-memory only).
 */
@Serializable
data class PendingDeliveryValidation(
    val id: String,
    val routeId: String,
    val deliveryId: String,
    val recipientName: String,
    val address: String,
    val status: DeliveryOutcome,
    val comment: String?,
    /** Retour 27/10 (point 3) — null when validated without photo (driver's own route); items
     * queued before always carry one. */
    val photoFileName: String?,
    /** ISO-8601 instant of the local tap (device clock) — becomes the `occurredAt` form field on replay. */
    val occurredAt: String,
    /** "16/06/2026 · 16:47" — same client-side stamp precedent as `DeliveryProofState.photoCapturedAtLabel`. */
    val capturedAtLabel: String,
    val syncState: PendingSyncState = PendingSyncState.Pending,
    /** Last replay failure's machine code (e.g. `error.conflict.DeliveryStatus`) — shown verbatim on the retry row (mockup screen 4b), not localized: it's a technical detail for the driver, not a user-facing message. */
    val lastErrorCode: String? = null,
)
